/* 오버레이 미리보기 (/ds/overlays)
 *
 * 시트·모달은 특정 상태에서만 뜨는 탓에 화면 검사에서 빠지기 쉽습니다.
 * 하나씩 눌러 열어 볼 수 있게 한 자리에 모았습니다. 실제 데이터는 쓰지 않습니다.
 */
import { useState } from 'react';
import { AppScreen, Section, SectionHeader, Button } from 'components/ds';
import { InviteFromPastMeetings } from 'components/meeting/InviteFromPastMeetings';
import { AttendancePrompt } from 'components/meeting/AttendancePrompt';
import RaceFinishModal, { RaceFinishRow } from 'components/meeting/RaceFinishModal';
import ParticipantMapSheet from 'components/meeting/ParticipantMapSheet';
import RaceBottomSheet from 'components/meeting/RaceBottomSheet';
import RunnerAvatar from 'components/meeting/RunnerAvatar';
import { TreatCelebrationBanner } from 'components/common/TreatCelebrationBanner';
import ParticipantSourceSheet from 'components/common/ParticipantSourceSheet';
import { BlockConfirmModal } from 'components/common/BlockConfirmModal';
import { ExternalMapSheet } from 'components/map/ExternalMapSheet';
import styles from './OverlayPreview.module.css';

const RACE_ROWS: RaceFinishRow[] = [
    { userNo: 1, nickname: '윤재권', rank: 1, arrived: true, lateMinutes: 0, isLast: false },
    { userNo: 2, nickname: '이수진', rank: 2, arrived: true, lateMinutes: 0, isLast: false },
    { userNo: 3, nickname: '박민수', rank: 3, arrived: true, lateMinutes: 4, isLast: false },
    { userNo: 4, nickname: '최지영', rank: 4, arrived: false, lateMinutes: 18, isLast: true,
      excuseReason: '버스가 안 와요', excuseApproved: false },
];

const PARTICIPANTS = [
    { userNo: 1, nickname: '윤재권', attendanceStatus: 'ATTENDING', locationSharing: true, isHost: true, isMe: true },
    { userNo: 2, nickname: '이수진', attendanceStatus: 'ATTENDING', locationSharing: false },
    { userNo: 3, nickname: '박민수', attendanceStatus: 'UNDECIDED', locationSharing: false },
    { userNo: 4, nickname: '최지영', attendanceStatus: 'NOT_ATTENDING', locationSharing: false },
];

const RACE_LOCS = {
    1: { latitude: 37.5601, longitude: 126.9250, timestamp: Date.now(), history: [] },
    2: { latitude: 37.5650, longitude: 126.9300, timestamp: Date.now(), history: [] },
    3: { latitude: 37.5700, longitude: 126.9400, timestamp: Date.now(), history: [] },
};

type Which =
    | null | 'invitePast' | 'attend' | 'raceFinish' | 'participantSheet'
    | 'raceSheet' | 'treat' | 'participantSource' | 'block' | 'externalMap';

export const OverlayPreview = () => {
    const [open, setOpen] = useState<Which>(null);
    const close = () => setOpen(null);

    const items: Array<{ key: Which; label: string }> = [
        { key: 'invitePast', label: '지난 모임에서 초대' },
        { key: 'attend', label: '참석 여부 묻기' },
        { key: 'raceFinish', label: '레이스 결과' },
        { key: 'participantSheet', label: '참여자 하단 시트' },
        { key: 'raceSheet', label: '도착 레이스 패널' },
        { key: 'treat', label: '내가 쏜다 축하' },
        { key: 'participantSource', label: '게임 참여자 고르기' },
        { key: 'block', label: '차단 확인' },
        { key: 'externalMap', label: '다른 지도 앱' },
    ];

    return (
        <AppScreen title="오버레이 미리보기" bar="sub">
            <Section spacing="tight">
                <SectionHeader
                    title="오버레이"
                    sub="상태를 만들기 어려운 시트·모달을 여기서 바로 열어 봅니다."
                />
                <div className={styles.grid}>
                    {items.map(it => (
                        <Button key={it.key} variant="secondary" onClick={() => setOpen(it.key)}>
                            {it.label}
                        </Button>
                    ))}
                </div>
            </Section>

            <Section divided last>
                <SectionHeader title="달리는 사람" />
                <div className={styles.runners}>
                    {(['arrived', 'rush', 'panic', 'stopped'] as const).map((state, i) => (
                        <RunnerAvatar
                            key={state}
                            state={state}
                            rank={i + 1}
                            size={44}
                            nickname={`주자${i + 1}`}
                        />
                    ))}
                </div>
            </Section>

            {open === 'treat' && (
                <TreatCelebrationBanner
                    items={[{ meetingName: '가을밤 와인', paidByNickname: '이수진',
                        storeName: '보떼', round: 1, onClick: close }]}
                />
            )}

            <InviteFromPastMeetings
                open={open === 'invitePast'}
                onClose={close}
                targetMeetingId="preview"
                currentUserNo={1}
            />

            <AttendancePrompt
                visible={open === 'attend'}
                meetingName="가을밤 와인 한 잔"
                meetingDate="2026년 9월 12일 (토)"
                meetingTime="오후 7:00"
                meetingLocation="연남동 와인바 보떼"
                onSubmit={async () => close()}
                onDismiss={close}
            />

            <RaceFinishModal
                open={open === 'raceFinish'}
                rows={RACE_ROWS}
                currentUserNo={3}
                lateFeeAmount={5000}
                shuttlePenaltyEnabled
                isHost
                onApproveExcuse={() => {}}
                onClose={close}
            />

            {open === 'participantSheet' && (
                <ParticipantMapSheet participants={PARTICIPANTS} defaultExpanded />
            )}

            {open === 'raceSheet' && (
                <RaceBottomSheet
                    participants={PARTICIPANTS.map(p => ({
                        userNo: p.userNo, nickname: p.nickname, attendanceStatus: p.attendanceStatus,
                    }))}
                    memberLocations={RACE_LOCS}
                    destination={{ latitude: 37.5602, longitude: 126.9256 }}
                    meetingStartMs={Date.now() + 1000 * 60 * 20}
                    currentUserNo={1}
                    lateFeeAmount={5000}
                    shuttlePenaltyEnabled
                    isHost
                    onToggleShuttle={() => {}}
                    onReportExcuse={() => {}}
                    onClose={close}
                    defaultExpanded
                />
            )}

            <ParticipantSourceSheet
                open={open === 'participantSource'}
                onClose={close}
                onPick={close}
            />

            <BlockConfirmModal
                isOpen={open === 'block'}
                targetName="박민수"
                onClose={close}
                onConfirm={close}
            />

            <ExternalMapSheet
                visible={open === 'externalMap'}
                placeName="연남동 와인바 보떼"
                address="서울 마포구 연희로1길 41"
                lat={37.5602}
                lng={126.9256}
                onClose={close}
            />
        </AppScreen>
    );
};
